import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Play, Pause, Clock, Trophy, Dumbbell, TrendingUp, ChevronDown, ChevronUp, Save, X, History, Calendar, Flame, Timer, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'active_workout_data';

const Tracker = () => {
  const [activeView, setActiveView] = useState('workout');
  const [workoutActive, setWorkoutActive] = useState(false);
  const [workoutName, setWorkoutName] = useState('');
  const [exercises, setExercises] = useState([]);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState(null);
  const [personalRecords, setPersonalRecords] = useState([]);
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [timer, setTimer] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [restTimer, setRestTimer] = useState(0);
  const [restTimerActive, setRestTimerActive] = useState(false);
  const [restDuration, setRestDuration] = useState(90);
  const timerRef = useRef(null);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  // === PERSISTENCIA: Guardar en localStorage automáticamente ===
  const saveToStorage = useCallback((data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...data,
      savedAt: Date.now()
    }));
  }, []);

  // Restaurar entrenamiento al cargar
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        // Solo restaurar si tiene menos de 4 horas
        if (Date.now() - data.savedAt < 4 * 60 * 60 * 1000) {
          setWorkoutActive(true);
          setWorkoutName(data.workoutName || '');
          setExercises(data.exercises || []);
          setTimer(data.timer || 0);
          setTimerRunning(true);
          toast.success('Entrenamiento restaurado');
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    fetchExerciseLibrary();
    fetchStats();
    fetchPersonalRecords();
    fetchWorkoutHistory();
  }, []);

  // Guardar cada vez que cambian los ejercicios
  useEffect(() => {
    if (workoutActive) {
      saveToStorage({ workoutName, exercises, timer });
    }
  }, [exercises, workoutName, workoutActive, saveToStorage]);

  // Timer principal
  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => {
        setTimer(prev => {
          const newVal = prev + 1;
          // Actualizar timer en storage cada 10s
          if (newVal % 10 === 0) {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
              const data = JSON.parse(saved);
              data.timer = newVal;
              localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            }
          }
          return newVal;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning]);

  // Rest timer
  useEffect(() => {
    let interval;
    if (restTimerActive && restTimer > 0) {
      interval = setInterval(() => {
        setRestTimer(prev => {
          if (prev <= 1) {
            setRestTimerActive(false);
            if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
            toast.success('Descanso terminado!');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [restTimerActive, restTimer]);

  const fetchExerciseLibrary = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/exercises/library`);
      setExerciseLibrary(response.data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/workouts/stats`, { headers });
      setStats(response.data);
    } catch (error) {}
  };

  const fetchPersonalRecords = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/workouts/personal-records`, { headers });
      setPersonalRecords(response.data);
    } catch (error) {}
  };

  const fetchWorkoutHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/workouts`, { headers });
      setWorkoutHistory(response.data);
    } catch (error) {}
  };

  const startWorkout = () => {
    setWorkoutActive(true);
    setTimerRunning(true);
    setTimer(0);
    setExercises([]);
    setWorkoutName(`Entrenamiento ${new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })}`);
  };

  const cancelWorkout = () => {
    if (window.confirm('Cancelar entrenamiento? Se perdera todo el progreso.')) {
      setWorkoutActive(false);
      setTimerRunning(false);
      setTimer(0);
      setExercises([]);
      setWorkoutName('');
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const addExerciseToWorkout = (exercise) => {
    setExercises([...exercises, {
      exercise_id: exercise.id,
      name: exercise.name,
      category: exercise.category,
      sets: [{ reps: '', weight: '', completed: false }],
      notes: ''
    }]);
    setShowExerciseModal(false);
    setSearchTerm('');
  };

  const addCustomExercise = () => {
    if (!searchTerm.trim()) return;
    setExercises([...exercises, {
      exercise_id: searchTerm.toLowerCase().replace(/\s+/g, '_'),
      name: searchTerm,
      category: 'personalizado',
      sets: [{ reps: '', weight: '', completed: false }],
      notes: ''
    }]);
    setShowExerciseModal(false);
    setSearchTerm('');
  };

  const removeExercise = (index) => {
    setExercises(exercises.filter((_, i) => i !== index));
  };

  const addSet = (exerciseIndex) => {
    const updated = [...exercises];
    const lastSet = updated[exerciseIndex].sets[updated[exerciseIndex].sets.length - 1];
    updated[exerciseIndex].sets.push({ reps: lastSet?.reps || '', weight: lastSet?.weight || '', completed: false });
    setExercises(updated);
  };

  const removeSet = (exerciseIndex, setIndex) => {
    const updated = [...exercises];
    if (updated[exerciseIndex].sets.length > 1) {
      updated[exerciseIndex].sets.splice(setIndex, 1);
      setExercises(updated);
    }
  };

  const updateSet = (exerciseIndex, setIndex, field, value) => {
    const updated = [...exercises];
    updated[exerciseIndex].sets[setIndex][field] = value;
    setExercises(updated);
  };

  const toggleSetComplete = (exerciseIndex, setIndex) => {
    const updated = [...exercises];
    updated[exerciseIndex].sets[setIndex].completed = !updated[exerciseIndex].sets[setIndex].completed;
    setExercises(updated);
    if (updated[exerciseIndex].sets[setIndex].completed) {
      setRestTimer(restDuration);
      setRestTimerActive(true);
    }
  };

  const saveWorkout = async () => {
    if (exercises.length === 0) { toast.error('Agrega al menos un ejercicio'); return; }
    setTimerRunning(false);
    try {
      await axios.post(`${BACKEND_URL}/api/workouts`, {
        name: workoutName || 'Entrenamiento',
        exercises, date: new Date().toISOString(),
        duration_minutes: Math.round(timer / 60), notes: ''
      }, { headers });
      toast.success('Entrenamiento guardado!');
      setWorkoutActive(false);
      setTimer(0);
      setExercises([]);
      setWorkoutName('');
      localStorage.removeItem(STORAGE_KEY);
      fetchStats();
      fetchPersonalRecords();
      fetchWorkoutHistory();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return hrs > 0 
      ? `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      : `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const filteredExercises = exerciseLibrary.filter(ex => 
    ex.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ex.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ex.muscle_group?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [...new Set(exerciseLibrary.map(ex => ex.category))];

  return (
    <div className="min-h-screen bg-[#09090b] py-20 pb-32" data-testid="tracker-page">
      <div className="max-w-3xl mx-auto px-4">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Tracker de Progresion</h1>
          <div className="flex gap-1 bg-zinc-900 rounded-full p-1 border border-zinc-800">
            {[
              { id: 'workout', icon: Dumbbell, label: 'Entrenar' },
              { id: 'history', icon: History, label: 'Historial' },
              { id: 'records', icon: Trophy, label: 'Records' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveView(tab.id)}
                className={`px-3 py-2 rounded-full text-xs font-medium transition-all ${
                  activeView === tab.id ? 'bg-pink-600 text-white' : 'text-zinc-400 hover:bg-zinc-800'
                }`}>
                <tab.icon className="w-3.5 h-3.5 inline mr-1" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-2 mb-6">
            {[
              { value: stats.total_workouts, label: 'Total', color: 'pink' },
              { value: stats.current_streak, label: 'Racha', color: 'orange' },
              { value: stats.workouts_this_week, label: 'Semana', color: 'blue' },
              { value: stats.total_volume ? `${(stats.total_volume / 1000).toFixed(0)}k` : '0', label: 'kg', color: 'green' },
            ].map((s, i) => (
              <div key={i} className="bg-zinc-900 rounded-xl p-3 border border-zinc-800 text-center">
                <p className={`text-lg font-bold text-${s.color}-500`}>{s.value}</p>
                <p className="text-[10px] text-zinc-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* ENTRENAR */}
        {activeView === 'workout' && (
          <>
            {!workoutActive ? (
              <div className="bg-zinc-900 rounded-3xl p-8 border border-zinc-800 text-center">
                <Dumbbell className="w-14 h-14 text-pink-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2 text-white">Lista para entrenar?</h2>
                <p className="text-zinc-400 mb-6 text-sm">Registra ejercicios, series, peso y repeticiones</p>
                <Button onClick={startWorkout} className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8 py-5 font-bold">
                  <Play className="w-5 h-5 mr-2" /> Iniciar Entrenamiento
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Header entrenamiento activo */}
                <div className="bg-gradient-to-r from-pink-600 to-pink-700 rounded-2xl p-4 text-white">
                  <Input value={workoutName} onChange={(e) => setWorkoutName(e.target.value)}
                    className="bg-white/20 border-0 text-white placeholder:text-white/70 font-bold rounded-xl mb-3" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 bg-white/20 rounded-xl px-3 py-2">
                      <Clock className="w-4 h-4" />
                      <span className="text-lg font-mono font-bold">{formatTime(timer)}</span>
                    </div>
                    <button onClick={() => setTimerRunning(!timerRunning)}
                      className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                      {timerRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {/* Rest Timer */}
                  {restTimerActive && (
                    <div className="mt-3 bg-white/20 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Timer className="w-4 h-4" />
                        <span className="text-sm font-medium">Descanso</span>
                      </div>
                      <span className="text-xl font-mono font-bold">{formatTime(restTimer)}</span>
                      <button onClick={() => setRestTimerActive(false)} className="text-white/70"><X className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>

                {/* Configurar descanso */}
                <div className="flex items-center gap-2 px-1">
                  <Timer className="w-4 h-4 text-zinc-500" />
                  <span className="text-xs text-zinc-500">Descanso:</span>
                  {[60, 90, 120, 150].map(s => (
                    <button key={s} onClick={() => setRestDuration(s)}
                      className={`px-2 py-1 rounded-md text-xs ${restDuration === s ? 'bg-pink-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                      {s}s
                    </button>
                  ))}
                </div>

                {/* Lista de ejercicios - TODOS EXPANDIDOS */}
                {exercises.map((exercise, exIndex) => (
                  <div key={exIndex} className="bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden">
                    <div className="p-3 flex items-center justify-between bg-zinc-900">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-pink-500/20 rounded-lg flex items-center justify-center">
                          <Dumbbell className="w-4 h-4 text-pink-500" />
                        </div>
                        <div>
                          <h3 className="font-bold text-white text-sm">{exercise.name}</h3>
                          <p className="text-[10px] text-zinc-500">{exercise.category}</p>
                        </div>
                      </div>
                      <button onClick={() => removeExercise(exIndex)} className="p-1.5 text-zinc-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Sets - SIEMPRE VISIBLE */}
                    <div className="px-3 pb-3">
                      <div className="grid grid-cols-12 gap-1 pb-2 text-[10px] font-bold text-zinc-500 uppercase">
                        <div className="col-span-1">#</div>
                        <div className="col-span-3">Anterior</div>
                        <div className="col-span-3 text-center">Kg</div>
                        <div className="col-span-3 text-center">Reps</div>
                        <div className="col-span-2 text-center">OK</div>
                      </div>
                      
                      {exercise.sets.map((set, setIndex) => (
                        <div key={setIndex} className={`grid grid-cols-12 gap-1 items-center py-1.5 ${set.completed ? 'bg-green-500/10 -mx-3 px-3 rounded-lg' : ''}`}>
                          <div className="col-span-1">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${set.completed ? 'bg-green-500 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                              {setIndex + 1}
                            </span>
                          </div>
                          <div className="col-span-3 text-xs text-zinc-500">-</div>
                          <div className="col-span-3">
                            <Input type="number" value={set.weight} onChange={(e) => updateSet(exIndex, setIndex, 'weight', e.target.value)}
                              placeholder="0" className="text-center h-8 text-sm rounded-lg bg-zinc-800 border-zinc-700 text-white" />
                          </div>
                          <div className="col-span-3">
                            <Input type="number" value={set.reps} onChange={(e) => updateSet(exIndex, setIndex, 'reps', e.target.value)}
                              placeholder="0" className="text-center h-8 text-sm rounded-lg bg-zinc-800 border-zinc-700 text-white" />
                          </div>
                          <div className="col-span-2 flex justify-center gap-1">
                            <button onClick={() => toggleSetComplete(exIndex, setIndex)}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                                set.completed ? 'bg-green-500 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'
                              }`}>
                              {set.completed ? '✓' : '○'}
                            </button>
                            <button onClick={() => removeSet(exIndex, setIndex)}
                              className="w-7 h-7 rounded-lg flex items-center justify-center bg-zinc-800 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 text-xs">
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      <button onClick={() => addSet(exIndex)}
                        className="w-full mt-2 py-2 rounded-xl border border-zinc-700 text-zinc-400 hover:border-pink-500/50 hover:text-pink-400 text-xs font-medium">
                        + Serie
                      </button>
                    </div>
                  </div>
                ))}

                {/* Agregar ejercicio */}
                <button onClick={() => setShowExerciseModal(true)}
                  className="w-full bg-zinc-900 rounded-2xl p-4 border-2 border-dashed border-zinc-700 hover:border-pink-500/50 flex items-center justify-center gap-2 text-zinc-400 hover:text-pink-400">
                  <Plus className="w-5 h-5" /> Agregar Ejercicio
                </button>

                {/* Acciones */}
                <div className="flex gap-3 pt-2">
                  <Button onClick={cancelWorkout} variant="outline" className="flex-1 rounded-full py-5 border-zinc-700 text-zinc-300">
                    Cancelar
                  </Button>
                  <Button onClick={saveWorkout} className="flex-1 bg-pink-600 hover:bg-pink-500 text-white rounded-full py-5 font-bold" disabled={exercises.length === 0}>
                    <Save className="w-4 h-4 mr-2" /> Finalizar
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* HISTORIAL */}
        {activeView === 'history' && (
          <div className="space-y-3">
            {workoutHistory.length === 0 ? (
              <div className="bg-zinc-900 rounded-3xl p-8 text-center border border-zinc-800">
                <History className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold mb-1 text-white">Sin entrenamientos</h3>
                <p className="text-zinc-500 text-sm">Completa tu primer entrenamiento</p>
              </div>
            ) : (
              workoutHistory.map((workout, idx) => (
                <div key={idx} className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-white">{workout.name || 'Entrenamiento'}</h3>
                      <p className="text-xs text-zinc-500">
                        {new Date(workout.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />{workout.duration_minutes || 0}min
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {workout.exercises?.map((ex, i) => (
                      <span key={i} className="bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded-full text-xs">
                        {ex.name} ({ex.sets?.length}x)
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-4 mt-2 pt-2 border-t border-zinc-800 text-xs text-zinc-500">
                    <span>{workout.total_sets || 0} series</span>
                    <span>{workout.total_volume ? workout.total_volume.toFixed(0) : 0} kg vol.</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* RECORDS */}
        {activeView === 'records' && (
          <div className="space-y-3">
            {personalRecords.length === 0 ? (
              <div className="bg-zinc-900 rounded-3xl p-8 text-center border border-zinc-800">
                <Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold mb-1 text-white">Sin records</h3>
                <p className="text-zinc-500 text-sm">Entrena para establecer records</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {personalRecords.map((record, idx) => (
                  <div key={idx} className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-5 h-5 text-yellow-500" />
                      <h3 className="font-bold text-white text-sm">{record.exercise_name}</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-zinc-800 rounded-lg p-2">
                        <p className="text-lg font-bold text-pink-500">{record.best_weight}</p>
                        <p className="text-[10px] text-zinc-500">kg</p>
                      </div>
                      <div className="bg-zinc-800 rounded-lg p-2">
                        <p className="text-lg font-bold text-pink-500">{record.best_reps}</p>
                        <p className="text-[10px] text-zinc-500">reps</p>
                      </div>
                      <div className="bg-zinc-800 rounded-lg p-2">
                        <p className="text-lg font-bold text-pink-500">{record.best_1rm}</p>
                        <p className="text-[10px] text-zinc-500">1RM</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal ejercicios */}
      {showExerciseModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-zinc-900 border border-zinc-800 rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden">
            <div className="p-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-bold text-white">Agregar Ejercicio</h2>
                <button onClick={() => { setShowExerciseModal(false); setSearchTerm(''); }}
                  className="p-2 hover:bg-zinc-800 rounded-full text-zinc-400"><X className="w-5 h-5" /></button>
              </div>
              <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar ejercicio..." className="rounded-xl bg-zinc-800 border-zinc-700 text-white" autoFocus />
            </div>
            
            <div className="overflow-y-auto max-h-[65vh] p-4">
              {searchTerm && filteredExercises.length === 0 && (
                <button onClick={addCustomExercise}
                  className="w-full p-3 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/30 rounded-xl mb-4">
                  <p className="font-medium text-pink-400 text-sm">+ Crear "{searchTerm}"</p>
                </button>
              )}
              
              {categories.map(category => {
                const catExercises = filteredExercises.filter(ex => ex.category === category);
                if (catExercises.length === 0) return null;
                return (
                  <div key={category} className="mb-4">
                    <h3 className="text-xs font-bold text-zinc-500 uppercase mb-2 px-1">{category}</h3>
                    <div className="space-y-0.5">
                      {catExercises.map(exercise => (
                        <button key={exercise.id} onClick={() => addExerciseToWorkout(exercise)}
                          className="w-full p-2.5 hover:bg-zinc-800 rounded-xl text-left flex items-center gap-2">
                          <Dumbbell className="w-4 h-4 text-pink-500 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-white text-sm">{exercise.name}</p>
                            <p className="text-[10px] text-zinc-500">{exercise.muscle_group}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tracker;
